import type {
  CompRef,
  PossibleViewModes,
  Style,
  StyleRef,
  ViewModes,
} from 'types/documentServices';
import type {
  CompRef as CompRefData,
  DialogOptions,
} from '@wix/a11y-adapters/editor';
import type { EditorAPI } from '#packages/editorAPI';

export const CLASSIC_ENTRY_POINT = 'editor-top-bar';
export const EDITOR_APP_ID = 'editorAppId';

export const viewModes: ViewModes = {
  MOBILE: 'MOBILE',
  DESKTOP: 'DESKTOP',
};

export const isHamburgerMenu = (editorAPI: EditorAPI, compRef: CompRef) => {
  return [
    'wixui.HamburgerMenuContainer',
    'wixui.HamburgerOverlay',
    'wixui.HamburgerMenuRoot',
  ].includes(editorAPI.components.getType(compRef));
};

export const getDisplayedComponents = (
  editorAPI: EditorAPI,
  pageId: string,
) => {
  const predicate = (compRef: CompRef) => {
    return !isHamburgerMenu(editorAPI, compRef);
  };
  const rootComponentsDesktop = editorAPI.components.getRootComponents(pageId);
  const displayedComponents =
    editorAPI.components.getChildrenRecursivelyWithResolvers(
      rootComponentsDesktop,
      predicate,
    );
  return displayedComponents;
};

export const isImageXData = (data: any) => {
  return data && data.type === 'ImageX';
};

export const transformStyle = (style: Style) => {
  return {
    properties: style.properties,
    propertiesSource: style.propertiesSource,
    propertiesOverride: style.propertiesOverride,
    groups: style.groups,
    background: style.background
      ? { gradient: style.background.gradient ?? undefined }
      : undefined,
  };
};

export const convertCompRefTypeToString = (compRefData: CompRef) => {
  const type = viewModes[compRefData.type as PossibleViewModes];
  const compRef = {
    ...compRefData,
    type,
  };
  return compRef;
};

export const convertCompRefTypeToViewMode = (compRef: CompRefData) => {
  const compRefData = {
    ...compRef,
    type: compRef.type as PossibleViewModes,
  };
  return compRefData;
};

export const convertStyleTypeToWizard = (
  styleThemeMap: Record<string, StyleRef>,
) => {
  const entries = Object.entries(styleThemeMap);

  const [secondKey, secondStyle] = entries[1];
  const style = {
    ...secondStyle,
    metaData: {
      ...secondStyle.metaData,
      schemaVersion: secondStyle.metaData.schemaVersion as '1.0' | '2.0',
    },
  };
  return {
    [secondKey]: style,
  };
};

export const showErrorPanel = async (
  editorAPI: EditorAPI,
  dialogOptions: DialogOptions,
) => {
  const result = await editorAPI.platform.panels.openErrorPanel({
    headerText: dialogOptions.title,
    shouldShowIllustration: false,
    topDescriptionText: dialogOptions.content,
    secondaryActionText: dialogOptions.secondaryButtonText,
    mainActionText: dialogOptions.primaryButtonText,
    helpId: dialogOptions.articleId,
    primaryAction: () => dialogOptions.primaryButtonOnClick(),
    secondaryAction: () => dialogOptions.secondaryButtonOnClick(),
  });
  return result;
};

export const showConfirmationPanel = async (
  editorAPI: EditorAPI,
  dialogOptions: DialogOptions,
) => {
  const result = await editorAPI.platform.panels.openConfirmationPanel({
    headerText: dialogOptions.title,
    shouldShowIllustration: false,
    descriptionText: dialogOptions.content,
    secondaryActionText: dialogOptions.secondaryButtonText,
    mainActionText: dialogOptions.primaryButtonText,
    helpId: dialogOptions.articleId,
    primaryAction: () => dialogOptions.primaryButtonOnClick(),
    secondaryAction: () => dialogOptions.secondaryButtonOnClick(),
  });
  return result;
};
